import React, { useEffect, useState } from "react";
import { IconTag } from "assets/svg";
import { message, Image, Button, Carousel } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import {
  fetchProductDetail,
  fetchProducts,
  productCleanData,
} from "reducers/productSlice";
import {
  Product,
  ProductFilter,
  Transaction,
  TransactionParams,
} from "common/interfaces";
import {
  DISPOSAL_TYPE,
  PRODUCT_CATEGORY,
  PRODUCT_STATE,
  SHIP_CHARGE_TYPE,
  SHIP_DAY,
  SORT,
} from "common/constants";
import {
  getLabelFromValue,
  formatProductQuantity,
  parseErrorMessage,
  productPrice,
} from "common/utils";
import { FILE_BASE_URL } from "config/apiConfig";
import { ProductItem } from "components/ProductItem";
import CustomConfirmModal from "components/CustomConfirmModal";
import { TransactionStatusEnum } from "common/enums/TransactionStatus.enum";
import {
  fetchTransactionDetail,
  transactionCleanData,
  updateTransaction,
} from "reducers/transactionSlice";
import { ProductStatusEnum } from "common/enums/Product.enum";
import CustomBreadCrumb from "components/CustomBreadCrumb";
import { useCommonContext } from "contexts/CommonContext";
import { useGetProductCompanyQuery } from "reducers/productApi";
import ProductSellerCompanyCard from "components/productSellerCompanyCard/ProductSellerCompanyCard";

const ProductDetail = () => {
  const { setHeaderTitle, setHeaderTitleMobile } = useCommonContext();
  const dispatch = useDispatch<AppDispatch>();
  const { productDetailData, error: productErr } = useSelector(
    (state: RootState) => state.product,
  );
  const {
    transactionDetailData,
    updateTransactionData,
    error: transactionErr,
  } = useSelector((state: RootState) => state.transaction);
  const { userData } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const controller = new AbortController();
  const { code } = useParams();
  const productDetailInfo: Product | undefined = productDetailData?.data;
  const transactionDetailInfo: Transaction | undefined =
    transactionDetailData?.data;
  const isSold = productDetailInfo?.status === ProductStatusEnum.SOLD;
  const isCanceled = (): boolean => {
    return (
      transactionDetailData?.data?.status ===
        TransactionStatusEnum.BUYER_ACCEPT_CANCEL ||
      transactionDetailData?.data?.status ===
        TransactionStatusEnum.SELLER_ACCEPT_CANCEL
    );
  };
  const isDraft = productDetailInfo?.status === ProductStatusEnum.DRAFT;
  const [openConfirmModalRequest, setOpenConfirmModalRequest] = useState(false);

  const transactionInfo: TransactionParams = {
    product_id: Number(productDetailInfo?.id),
    buyer_id: Number(userData?.id),
    seller_id: Number(productDetailInfo?.company_id),
  };

  const productList: ProductFilter = {
    search: "",
    category_id: [],
    state: [],
    disposal_type: [],
    sort: SORT.NEWEST.value,
  };

  const { data: productCompanyData, error: productCompanyErr } =
    useGetProductCompanyQuery({
      filter: {
        company_id: productDetailInfo?.company_id.toString(),
      },
    });

  const transactionDetail = (type: number) => {
    if (type === TransactionStatusEnum.REQUEST) {
      dispatch(
        updateTransaction({
          ...transactionInfo,
          status: TransactionStatusEnum.REQUEST,
        }),
      );
    } else if (type === TransactionStatusEnum.NEGOTIATE) {
      dispatch(
        updateTransaction({
          ...transactionInfo,
          status: TransactionStatusEnum.NEGOTIATE,
        }),
      );
    }
    setOpenConfirmModalRequest(false);
  };

  useEffect(() => {
    if (
      productDetailInfo?.id &&
      userData?.id &&
      productDetailInfo?.company_id
    ) {
      dispatch(fetchTransactionDetail(transactionInfo));
    }
  }, [productDetailInfo?.id, userData?.id, productDetailInfo?.company_id]);

  useEffect(() => {
    if (code) {
      dispatch(fetchProductDetail(code, controller.signal));
      dispatch(fetchProducts(productList, 0, controller.signal));
    }
  }, [code]);

  useEffect(() => {
    if (updateTransactionData?.success) {
      navigate(
        `/buyer/product/${code}/transaction/${userData?.id}/${productDetailInfo?.company_id}`,
      );
    }
  }, [updateTransactionData]);

  useEffect(() => {
    if (productErr) {
      message.error(productErr);
    }
  }, [productErr]);

  useEffect(() => {
    if (transactionErr) {
      message.error(transactionErr);
    }
  }, [transactionErr]);

  useEffect(() => {
    if (productCompanyErr)
      void message.error(parseErrorMessage(productCompanyErr));
  }, [productCompanyErr]);

  useEffect(() => {
    setHeaderTitle(
      `${productDetailInfo?.product_name}（出品ID #${productDetailInfo?.code}）`,
    );
    setHeaderTitleMobile(`${productDetailInfo?.product_name}`);
    return () => {
      setHeaderTitleMobile("");
    };
  }, [productDetailInfo, setHeaderTitle, setHeaderTitleMobile]);

  useEffect(() => {
    return () => {
      dispatch(productCleanData());
      dispatch(transactionCleanData());
    };
  }, []);

  return (
    <>
      <div className="inline-flex min-h-[924px] w-full min-w-0 self-stretch flex-col items-start justify-start gap-[30px] overflow-hidden bg-white p-[20px] pb-[100px] md:gap-10 md:p-10">
        <CustomBreadCrumb
          items={[
            {
              label: "商品検索",
              link: "/buyer/product/list",
            },
            {
              label: `${productDetailInfo?.product_name}（出品ID #${productDetailInfo?.code}）`,
              isCurrent: true,
            },
          ]}
        />
        <div className="flex min-w-0 self-stretch flex-col items-start justify-start gap-[30px] md:gap-12">
          <div className="inline-flex min-w-0 self-stretch flex-col items-start justify-start gap-[20px] xl:flex-row xl:gap-12">
            <div className="product-detail-image hidden w-full flex-col items-start justify-center gap-[10px] xl:inline-flex xl:w-auto xl:gap-2.5">
              <Image.PreviewGroup>
                <div className="w-full md:w-96 h-96 bg-primary-light rounded-md flex flex-col justify-center items-center gap-2.5 relative">
                  {isSold && (
                    <IconTag className="w-[70px] h-[70px] absolute top-[0px] left-[0px] z-[1]" />
                  )}
                  {isDraft && (
                    <div className="w-[70px] h-[70px] left-0 top-[-1px] absolute z-[1]">
                      <div className="w-[70px] h-[70px] left-0 top-0 absolute bg-red-dark [clip-path:polygon(0_0,100%_0,0_100%)]" />
                      <div className="left-[-3px] top-[35px] absolute origin-top-left -rotate-45 text-center justify-start text-white text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                        DRAFT
                      </div>
                    </div>
                  )}
                  <Image
                    src={`${FILE_BASE_URL}/${productDetailInfo?.images[0]?.image_url}`}
                    className="!w-full !h-full object-cover"
                  />
                </div>
                <div className="w-full inline-flex justify-start items-center gap-2.5">
                  {productDetailInfo?.images?.slice(1).map((i) => (
                    <div
                      key={i.id}
                      className="w-16 h-16 bg-primary-light rounded-md inline-flex flex-col justify-center items-center gap-2.5"
                    >
                      <Image
                        src={`${FILE_BASE_URL}/${i?.image_url}`}
                        className="!w-full !h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </Image.PreviewGroup>
            </div>
            <div className="product-detail-image w-full xl:hidden">
              <Carousel>
                {productDetailInfo?.images?.map((i) => (
                  <div key={i.id} className="h-[280px] relative">
                    {isSold && (
                      <IconTag className="w-[70px] h-[70px] absolute top-[0px] left-[0px] z-[1]" />
                    )}
                    {isDraft && (
                      <div className="w-[70px] h-[70px] left-0 top-[-1px] absolute z-[1]">
                        <div className="w-[70px] h-[70px] left-0 top-0 absolute bg-red-dark [clip-path:polygon(0_0,100%_0,0_100%)]" />
                        <div className="left-[-3px] top-[35px] absolute origin-top-left -rotate-45 text-center justify-start text-white text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                          DRAFT
                        </div>
                      </div>
                    )}
                    <Image
                      src={`${FILE_BASE_URL}/${i.image_url}`}
                      className="!w-full !h-full object-cover"
                    />
                  </div>
                ))}
              </Carousel>
            </div>
            <div className="inline-flex min-w-0 flex-1 flex-col items-start justify-start gap-[30px] md:gap-10">
              <div className="self-stretch flex flex-col justify-start items-start gap-[15px] md:gap-5">
                <div className="self-stretch flex flex-col justify-start items-start gap-[5px]">
                  <div className="self-stretch justify-start text-black text-3xl font-bold font-['Noto_Sans_JP'] leading-10">
                    {productDetailInfo?.product_name}
                  </div>
                  <div className="self-stretch justify-start text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                    {getLabelFromValue(
                      PRODUCT_CATEGORY,
                      productDetailInfo?.category_id,
                    )}
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-start items-center gap-[10px] md:gap-3.5">
                  <div className="justify-start text-black text-[36px] md:text-[40px] font-bold font-['Noto_Sans_JP'] leading-[64px]">
                    {productPrice(productDetailInfo)}
                  </div>
                  <div className="justify-start text-black text-[13px] md:text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                    {getLabelFromValue(
                      SHIP_CHARGE_TYPE,
                      productDetailInfo?.shipping_charge_type,
                    )}
                  </div>
                </div>
                <div className="inline-flex min-w-0 self-stretch flex-col items-start justify-start gap-[10px] 2xl:flex-row 2xl:gap-3.5">
                  {isSold ? (
                    <>
                      <Button
                        block
                        type="primary"
                        htmlType="button"
                        className="min-h-16 w-full bg-primary text-[18px] font-bold text-white md:text-xl 2xl:!w-[323px]"
                        disabled
                      >
                        売り切れました
                      </Button>
                      {transactionDetailInfo?.id ===
                        productDetailInfo?.transaction_id && (
                        <Button
                          block
                          type="primary"
                          htmlType="button"
                          className="min-h-16 w-full bg-white text-[18px] font-bold text-primary outline outline-2 outline-offset-[-2px] outline-primary md:text-xl 2xl:!w-[323px]"
                          onClick={() =>
                            navigate(
                              `/buyer/product/${code}/transaction/${userData?.id}/${productDetailInfo?.company_id}`,
                            )
                          }
                        >
                          取引画面を表示する
                        </Button>
                      )}
                    </>
                  ) : isCanceled() ? (
                    <>
                      <div className="w-full min-h-16 rounded text-red-dark border-[1px] border-red-dark flex justify-center items-center text-[18px] md:text-xl">
                        商品キャンセル済み
                      </div>
                    </>
                  ) : (
                    <>
                      {transactionDetailInfo ? (
                        <Link
                          className="w-full min-h-16 rounded text-primary border-primary border-[1px] flex justify-center items-center text-[18px] md:text-xl"
                          to={`/buyer/product/${productDetailInfo?.code}/transaction/${transactionDetailInfo.buyer_id}/${transactionDetailInfo.seller_id}`}
                        >
                          取引画面へ
                        </Link>
                      ) : (
                        <>
                          <Button
                            block
                            type="primary"
                            htmlType="button"
                            className="min-h-16 w-full bg-primary text-[18px] font-bold text-white md:text-xl 2xl:!w-[323px]"
                            onClick={() => setOpenConfirmModalRequest(true)}
                          >
                            購入申請をする
                          </Button>
                          <Button
                            block
                            type="primary"
                            htmlType="button"
                            className="min-h-16 w-full bg-white text-[18px] font-bold text-primary outline outline-2 outline-offset-[-2px] outline-primary md:text-xl 2xl:!w-[323px]"
                            onClick={() =>
                              transactionDetail(TransactionStatusEnum.NEGOTIATE)
                            }
                          >
                            お問い合わせする
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="self-stretch flex flex-col justify-center items-center gap-[30px] md:gap-7">
                <div className="self-stretch flex flex-col justify-start items-start gap-[10px] md:gap-3.5">
                  <div className="self-stretch justify-start text-black text-[18px] md:text-xl font-bold font-['Noto_Sans_JP'] leading-loose">
                    商品詳細
                  </div>
                  <div className="self-stretch justify-start text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                    {productDetailInfo?.description}
                  </div>
                </div>
                <div className="self-stretch flex flex-col justify-start items-start">
                  <div className="self-stretch py-3.5 bg-white border-t border-neutral-border inline-flex justify-start items-center gap-[10px] md:gap-3.5">
                    <div className="w-36 justify-start text-black text-[15px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                      数量
                    </div>
                    <div className="flex-1 justify-start text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                      {productDetailInfo
                        ? formatProductQuantity(productDetailInfo)
                        : ""}
                    </div>
                  </div>
                  <div className="self-stretch py-3.5 bg-white border-t border-neutral-border inline-flex justify-start items-center gap-[10px] md:gap-3.5">
                    <div className="w-36 justify-start text-black text-[15px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                      処理区分
                    </div>
                    <div className="flex-1 justify-start text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                      {getLabelFromValue(
                        DISPOSAL_TYPE,
                        productDetailInfo?.disposal_type,
                      )}
                    </div>
                  </div>
                  <div className="self-stretch py-3.5 bg-white border-t border-neutral-border inline-flex justify-start items-center gap-[10px] md:gap-3.5">
                    <div className="w-36 justify-start text-black text-[15px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                      商品の状態
                    </div>
                    <div className="flex-1 justify-start text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                      {getLabelFromValue(
                        PRODUCT_STATE,
                        productDetailInfo?.state,
                      )}
                    </div>
                  </div>
                  <div className="self-stretch py-3.5 bg-white border-t border-b border-neutral-border inline-flex justify-start items-center gap-[10px] md:gap-3.5">
                    <div className="w-36 justify-start text-black text-[15px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                      発送までの日数
                    </div>
                    <div className="flex-1 justify-start text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                      {getLabelFromValue(
                        SHIP_DAY,
                        productDetailInfo?.shipping_days,
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ProductSellerCompanyCard
            companyCode={productDetailInfo?.company.code}
            enableProfileLink
          />
          <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-10">
            <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
              <div
                data-プロパティ1="base"
                className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
              >
                <div className="w-1 self-stretch bg-primary rounded-sm" />
                <div className="flex-1 flex justify-start items-center gap-2.5">
                  <div className="flex justify-start items-center gap-[5px]">
                    <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                      この売り手企業の商品
                    </div>
                  </div>
                </div>
              </div>
              <div className="self-stretch flex flex-col justify-start items-start gap-[5px] md:gap-12">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-x-[15px] gap-y-[20px] md:gap-5">
                  {productCompanyData?.data?.items
                    ?.filter((item: Product) => item.code !== code)
                    ?.slice(0, 5)
                    .map((item: Product) => (
                      <ProductItem key={item.id} item={item} />
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomConfirmModal
        open={openConfirmModalRequest}
        title="この商品を購入申請しますか？"
        element={
          <div className="self-stretch px-5 pt-6 pb-5 bg-primary-light rounded-md inline-flex flex-col justify-start items-start">
            <div className="self-stretch text-center justify-start text-black text-[16px] md:text-lg font-normal font-['Noto_Sans_JP'] leading-7">
              販売価格
            </div>
            <div className="self-stretch text-center justify-start text-primary text-[36px] md:text-[40px] font-bold font-['Noto_Sans_JP'] leading-[64px]">
              {productPrice(productDetailInfo)}
            </div>
          </div>
        }
        description={`この商品に購入申請を行うと、
          売り手企業に「購入したい」という意思が伝えられます。
          出品者が申請を承認すると、購入が成立し取引が開始されます。`}
        note={`
          ※本操作だけでは購入は確定しません。出品者からの承認が必要です。
          ※承認後のやり取りはチャットで行います。`}
        onOk={() => transactionDetail(TransactionStatusEnum.REQUEST)}
        onCancel={() => setOpenConfirmModalRequest(false)}
        okText="購入申請をする"
        cancelText="戻る"
        okButtonBgColor="bg-primary"
      />
    </>
  );
};

export default ProductDetail;
